/*
 * Formatting for the dashboard. SOC teams correlate in UTC, so absolute
 * times are always UTC ISO-style ("2026-10-15 06:05 UTC"); lists show a
 * short relative age with the absolute time as the tooltip.
 */

const pad = (n) => String(n).padStart(2, '0');

function toDate(value) {
  if (!value) return null;
  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? null : d;
}

/** "2026-10-15" (UTC) */
export function isoDate(value) {
  const d = toDate(value);
  if (!d) return '';
  return `${d.getUTCFullYear()}-${pad(d.getUTCMonth() + 1)}-${pad(d.getUTCDate())}`;
}

/** "2026-10-15 06:05 UTC" */
export function utcStamp(value) {
  const d = toDate(value);
  if (!d) return '';
  return `${isoDate(d)} ${pad(d.getUTCHours())}:${pad(d.getUTCMinutes())} UTC`;
}

/** "06:05" (UTC) */
export function utcTime(value) {
  const d = toDate(value);
  return d ? `${pad(d.getUTCHours())}:${pad(d.getUTCMinutes())}` : '';
}

/** "Thursday 15 October 2026" in UTC, for the briefing masthead. */
export function longDate(value = new Date()) {
  const d = toDate(value);
  if (!d) return '';
  return d.toLocaleDateString('en-GB', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric', timeZone: 'UTC' });
}

/** Short age for dense lists: "now", "12m", "5h", "3d", then the ISO date. */
export function age(value) {
  const d = toDate(value);
  if (!d) return '';
  const m = Math.floor((Date.now() - d.getTime()) / 60000);
  if (m < 1) return 'now';
  if (m < 60) return `${m}m`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h`;
  const days = Math.floor(h / 24);
  if (days < 30) return `${days}d`;
  return isoDate(d);
}

/** "7m ago", "just now", or "on 2026-01-04" once age() falls back to a date. */
export function ago(value) {
  const a = age(value);
  if (!a) return '';
  if (a === 'now') return 'just now';
  return /^\d{4}-/.test(a) ? `on ${a}` : `${a} ago`;
}

/** Same 8-character brief ID the Inbox prints ("T-F2AFFC44"). */
export function briefId(attackId) {
  return `T-${String(attackId || '').slice(0, 8).toUpperCase()}`;
}

export function hostname(url) {
  try {
    return new URL(url).hostname.replace(/^www\./, '');
  } catch {
    return '';
  }
}

/** Strip HTML (including truncated tags) and decode common entities. */
export function cleanText(value) {
  if (!value) return '';
  return String(value)
    .replace(/<\/?[a-zA-Z][^>]*>/g, '')
    .replace(/<[^>]*$/g, '')
    .replace(/&nbsp;/g, ' ')
    .replace(/&amp;/g, '&')
    .replace(/&lt;/g, '<')
    .replace(/&gt;/g, '>')
    .replace(/&quot;/g, '"')
    .replace(/&#39;/g, "'")
    .replace(/\s+/g, ' ')
    .trim();
}

/** Markdown to plain prose for previews: no links, headings or emphasis marks. */
export function plainText(markdown) {
  return cleanText(
    String(markdown || '')
      .replace(/```[\s\S]*?```/g, ' ')
      .replace(/!\[[^\]]*\]\([^)]*\)/g, '')
      .replace(/\[([^\]]+)\]\([^)]*\)/g, '$1')
      .replace(/^\s{0,3}#{1,6}\s+/gm, '')
      .replace(/^\s*[-*+]\s+/gm, '')
      .replace(/(\*\*|__|\*|_|`)/g, '')
  );
}

/** Briefs whose own source list has several outlets show "N sources". */
export function sourceLabel(brief) {
  const n = Number(brief?.source_count) || (Array.isArray(brief?.sources) ? brief.sources.length : 0);
  if (n > 1) return `${n} sources`;
  return hostname(brief?.source_url) || (n === 1 ? '1 source' : '');
}

export function plural(n, word, many = `${word}s`) {
  return `${n} ${n === 1 ? word : many}`;
}
